<template>
  <CModal :show="show" :closeOnBackdrop="false" :centered="true" size="lg">
    <template #header>
      <h5 class="modal-title">{{ title }}</h5>
    </template>
    <div v-if="!loading">
      <form @submit.prevent="onSubmit">
        <div class="form-group">
          <label>{{ $t("models.market_plan.name") }}</label>
          <CInput v-model="form.to" type="text" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_wh.offset") }}</label>
          <CInput v-model.number="form.offset" type="number" min="0" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.family_address.address") }}</label>
          <CInput v-model="form.address" type="text" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.family_address.city") }}</label>
          <CInput v-model="form.city" type="text" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.family_address.country") }}</label>
          <select
            class="form-control"
            v-model="form.country"
            @change="onCountryChange(form.country)"
          >
            <option value="" disabled>
              {{ $t("family.settings.addresses.modal.country_select") }}
            </option>
            <option
              v-for="country in countryOptions"
              :key="country.code"
              :value="country.code"
            >
              {{ country.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>{{ $t("models.family_address.postal_code") }}</label>
          <v-select
            v-model="form.postal_code_id"
            :options="postalCodeOptions"
            :reduce="(option) => option.id"
            label="code"
            :disabled="!form.country"
            :filterable="true"
            class="bg-white"
          >
            <template #no-options>
              {{ $t("family.settings.addresses.modal.country_select") }}
            </template>
          </v-select>
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_wh.notifyto") }}</label>
          <CInput v-model="form.notifyto" type="email" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_wh.info") }}</label>
          <CTextarea v-model="form.info" rows="3" />
        </div>
      </form>

      <CAlert v-if="validationError" color="danger" :show.sync="showAlert">
        {{ validationError }}
      </CAlert>
    </div>
    <div
      v-else
      class="d-flex justify-content-center align-items-center"
      style="height: 200px"
    >
      <CSpinner color="primary" />
    </div>

    <template #footer>
      <CButton color="primary" variant="outline" @click.prevent="closeModal">
        {{ $t("common.editor.cancel") }}
      </CButton>
      <vue-ladda
        :loading="loading"
        data-style="zoom-in"
        button-class="btn btn-primary px-4"
        @click.prevent="onSubmit"
      >
        {{ $t("common.editor.confirm") }}
      </vue-ladda>
    </template>
  </CModal>
</template>

<script>
import vSelect from "vue-select";
import { apiPatch, apiPost, apiGet } from "../../../helpers/apiHelper";

export default {
  name: "WhModal",
  components: {
    vSelect,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: [Number, String],
      default: null,
    },
    values: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loading: true,
      validationError: null,
      showAlert: false,
      form: {
        to: null,
        offset: 0,
        address: null,
        city: null,
        country: null,
        postal_code_id: null,
        notifyto: null,
        info: null,
      },
      countryOptions: [],
      postalCodeOptions: [],
    };
  },

  watch: {
    show: {
      handler(isShown) {
        if (isShown) {
          if (this.values && Object.keys(this.values).length > 0) {
            this.populateForm(this.values);
          } else {
            this.resetForm();
          }
        }
      },
      immediate: false,
    },
    values: {
      handler(newValues) {
        if (newValues && Object.keys(newValues).length > 0) {
          this.populateForm(newValues);
        }
      },
      immediate: true,
    },
  },

  methods: {
    async loadCountries() {
      const response = await apiGet("geo_country", [], null);
      if (response && response.status === 200) {
        this.countryOptions = response.data.Items;
        this.loading = false;
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error loading countries",
          color: "danger",
          autohide: true,
        });
      }
    },

    async loadPostalCodes(countryCode) {
      if (!countryCode) {
        this.postalCodeOptions = [];
        return;
      }

      const response = await apiGet("geo_postalcode", [], null);
      if (response && response.status === 200) {
        this.postalCodeOptions = response.data.Items.filter(
          (item) => item.country_id === countryCode
        );
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error loading postal codes",
          color: "danger",
          autohide: true,
        });
      }
    },

    onCountryChange(countryCode) {
      this.form.postal_code_id = null;
      this.loadPostalCodes(countryCode);
    },

    populateForm(data) {
      if (data.address) {
        this.form.to = data.address.to || null;
        this.form.address = data.address.address || null;
        this.form.city = data.address.city || null;
        this.form.country = data.address.country || null;
        this.form.postal_code_id = data.address.postal_code?.id || null;

        if (this.form.country) {
          this.loadPostalCodes(this.form.country);
        }
      }
      this.form.offset = data.offset || 0;
      this.form.notifyto = data.notifyto || null;
      this.form.info = data.info || null;
    },

    async onSubmit() {
      this.validationError = null;
      this.showAlert = false;

      // Validation
      if (!this.form.to) {
        this.validationError = "Please enter a name";
        this.showAlert = true;
        return;
      }

      if (this.form.offset === null || this.form.offset === undefined) {
        this.validationError = "Please enter an offset";
        this.showAlert = true;
        return;
      }

      if (!this.form.address) {
        this.validationError = "Please enter an address";
        this.showAlert = true;
        return;
      }

      if (!this.form.city) {
        this.validationError = "Please enter a city";
        this.showAlert = true;
        return;
      }

      if (!this.form.country) {
        this.validationError = "Please select a country";
        this.showAlert = true;
        return;
      }

      if (!this.form.postal_code_id) {
        this.validationError = "Please select a postal code";
        this.showAlert = true;
        return;
      }

      const requestParams = {
        offset: this.form.offset,
        notifyto: this.form.notifyto,
        info: this.form.info,
        address: {
          to: this.form.to,
          address: this.form.address,
          city: this.form.city,
          country: this.form.country,
          postal_code: { id: this.form.postal_code_id },
        },
      };

      // check user permissions before submitting
      const perm = this.$store.state.connections.current.permissions.role_lg;
      if (perm !== 2) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.permissions"
        );
        this.showAlert = true;
        return;
      }

      let response;
      if (this.editMode && this.editId) {
        response = await apiPatch(
          "role_market_planningwh",
          this.editId,
          requestParams,
          [this.$store.state.role.id]
        );
      } else {
        response = await apiPost("role_market_planningwh", requestParams, [
          this.$store.state.role.id,
        ]);
      }

      if (response) {
        this.$emit("submitted");
        this.closeModal();
      }
    },

    closeModal() {
      this.$emit("update:show", false);
      this.validationError = null;
      this.showAlert = false;
    },

    resetForm() {
      this.form = {
        to: null,
        offset: 0,
        address: null,
        city: null,
        country: null,
        postal_code_id: null,
        notifyto: null,
        info: null,
      };
      this.validationError = null;
      this.showAlert = false;
    },
  },

  async created() {
    await this.loadCountries();
  },
};
</script>
